import { AlertComponent } from 'Components/HelperComponents/AlertComponent'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { ProposalsList } from 'NewVersion/components/Proposal/ProposalsList'
import Button from 'NewVersion/components/UI/Buttons/Button'
import Chips from 'NewVersion/components/UI/Chips/Chips'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import { EditIcon } from 'NewVersion/icons/EditIcon'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import 'NewVersion/styles/Dashboard.scss'
import * as path from 'Routes/Paths'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { getCurrentJWToken } from 'utils/utils'

/**
 * RepresentantDashboard - Dashboard for representants
 * Shows representant info, summary, all proposals table, and artists with their proposals
 */
export const RepresentantDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { repId } = useParams()
  const { edition, currentEdition, loading: editionLoading } = useEdition()
  const [artistsWithProposals, setArtistsWithProposals] = useState([])
  const [loadingProposals, setLoadingProposals] = useState(true)

  // Fetch representant data
  const {
    data: representant,
    isLoading: repLoading,
    error: repError,
  } = useGuillotina({
    path: repId ? `${edition}/${repId}` : null,
  })

  // Fetch artists for this representant
  const { data: artistsResult, isLoading: artistsLoading } = useGuillotina({
    path: repId
      ? `@search?type_name=Ens&tipus=artista&representant=/${edition}/${repId}&_sort_des=creation_date`
      : null,
  })

  const artists = artistsResult?.items || []

  // Fetch proposals for each artist
  useEffect(() => {
    const fetchProposalsForArtists = async () => {
      if (artists.length === 0) {
        setArtistsWithProposals([])
        setLoadingProposals(false)
        return
      }

      const token = getCurrentJWToken()
      const artistsData = await Promise.all(
        artists.map(async (artist) => {
          try {
            const proposalsResult = await fetchGuillotina({
              path: `${artist.path}/@search?type_name=Proposta&_sort_des=creation_date`,
              method: 'GET',
              token,
            })
            return {
              ...artist,
              proposals: proposalsResult?.items || [],
            }
          } catch (error) {
            console.error(`Error fetching proposals for artist ${artist.title}:`, error)
            return {
              ...artist,
              proposals: [],
            }
          }
        })
      )
      setArtistsWithProposals(artistsData)
      setLoadingProposals(false)
    }

    if (!artistsLoading) {
      fetchProposalsForArtists()
    }
  }, [artists, artistsLoading])

  // Calculate summary
  const summary = useMemo(() => {
    const allProposals = artistsWithProposals.flatMap((a) => a.proposals || [])
    return {
      totalArtists: artistsWithProposals.length,
      totalProposals: allProposals.length,
      drafts: allProposals.filter((p) => p.review_state !== 'presentada').length,
      submitted: allProposals.filter((p) => p.review_state === 'presentada').length,
    }
  }, [artistsWithProposals])

  if (editionLoading || repLoading) {
    return <LoadingComponent />
  }

  if (repError || !representant) {
    return <AlertComponent message={t('error_loading_representant')} type="error" />
  }

  const handleEditRepresentant = () => {
    navigate(
      `${path.EDIT_ENTITY_FORM_PATH.replace(':year', edition).replace(
        ':entityId',
        repId
      )}?from=gestio&repId=${repId}`
    )
  }

  const handleAddArtist = () => {
    // Navigate to artist creation with representant pre-selected
    navigate(
      `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&repId=${edition}/${repId}&createArtist=true&from=gestio`
    )
  }

  const handleNewProposal = (artistPath) => {
    navigate(
      `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&repId=${edition}/${repId}&artistId=${artistPath}&from=gestio`
    )
  }

  const handleEditArtist = (artistId) => {
    navigate(
      `${path.EDIT_ENTITY_FORM_PATH.replace(':year', edition).replace(
        ':entityId',
        artistId
      )}?from=gestio&repId=${repId}`
    )
  }

  const handleBack = () => {
    navigate(path.PROPOSALS_GESTIO_PATH)
  }

  return (
    <div className="dashboard">
      <GoBackButton onClick={handleBack} text={t('change_profile')} />

      {/* Representant Header */}
      <div className="dashboard__header">
        <div className="dashboard__avatar">{representant.title?.[0]?.toUpperCase() || '?'}</div>
        <div className="dashboard__info">
          <p className="dashboard__title">{representant.title}</p>
          <p className="dashboard__email">{representant.correu}</p>
          <p className="dashboard__summary-inline">
            {summary.totalArtists} {t('artists')} · {summary.totalProposals} {t('propostes')} ·{' '}
            {summary.drafts} {t('esborranys')} · {summary.submitted} {t('presentades')}
          </p>
        </div>
        <Chips
          text={t('editar')}
          icon={<EditIcon />}
          onClick={handleEditRepresentant}
          dataTest="btnEditRepresentant"
        />
      </div>

      {/* Artists Section */}
      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <p className="dashboard__section-title">{t('els_teus_artistes')}</p>
          {!currentEdition?.callIsFinished() && (
            <Button
              type="button"
              color="primaryOutlinedIcon outlined"
              size="small"
              onClick={handleAddArtist}
              dataTest="btnAddArtist"
            >
              + {t('afegir_artista')}
            </Button>
          )}
        </div>

        {loadingProposals || artistsLoading ? (
          <LoadingComponent />
        ) : artistsWithProposals.length === 0 ? (
          <p className="dashboard__no-representant">{t('no_artists_yet')}</p>
        ) : (
          <div className="dashboard__artists-list">
            {artistsWithProposals.map((artist) => (
              <div key={artist.path} className="dashboard__artist-section">
                <div className="dashboard__artist-header">
                  <div className="dashboard__artist-avatar">
                    {artist.title?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="dashboard__artist-info">
                    <p className="dashboard__artist-name">{artist.title}</p>
                    <p className="dashboard__artist-proposals-count">
                      {artist.proposals?.length || 0} {t('propostes')}
                    </p>
                  </div>
                  <Chips
                    text={t('editar')}
                    icon={<EditIcon />}
                    onClick={() => handleEditArtist(artist.path?.split('/').pop())}
                    dataTest={`btnEditArtist_${artist.path?.split('/').pop()}`}
                  />
                </div>
                <div className="dashboard__artist-proposals">
                  <ProposalsList
                    proposals={artist.proposals || []}
                    canSubmitProposal={!currentEdition?.callIsFinished()}
                    emptyMessage={t('no_proposals_for_artist')}
                  />
                  {!currentEdition?.callIsFinished() && (
                    <Button
                      type="button"
                      color="primary"
                      size="small"
                      onClick={() => handleNewProposal(artist.path)}
                      dataTest={`btnNewProposal_${artist.path?.split('/').pop()}`}
                    >
                      + {t('nova_proposta')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default RepresentantDashboard
